// src/pages/PartyDiscounts.jsx
import React, { useEffect, useState } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase"; // your firebase instance

export default function PartyDiscounts() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  // form state
  const [partyId, setPartyId] = useState(""); // blank => all parties
  const [brand, setBrand] = useState("");
  const [priceSource, setPriceSource] = useState("mrp");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "partyDiscounts"), orderBy("effectiveFrom", "desc"));
      const snap = await getDocs(q);
      setRules(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      alert("Failed to load rules");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRules(); }, []);

  const resetForm = () => {
    setPartyId(""); setBrand(""); setPriceSource("mrp"); setType("percent");
    setValue(""); setEffectiveFrom(""); setEffectiveTo(""); setEditingId(null);
  };

  const handleSave = async () => {
    if (!value || !effectiveFrom) return alert("Value and effective from required");
    setSaving(true);
    try {
      const payload = {
        partyId: partyId || null,
        brand: brand || null,
        priceSource,
        type,
        value: Number(value) || 0,
        effectiveFrom,
        effectiveTo: effectiveTo || null,
        createdAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, "partyDiscounts", editingId), payload);
      } else {
        await addDoc(collection(db, "partyDiscounts"), payload);
      }
      await fetchRules();
      resetForm();
    } catch (e) {
      console.error(e);
      alert("Save failed");
    } finally { setSaving(false); }
  };

  const handleEdit = (r) => {
    setEditingId(r.id);
    setPartyId(r.partyId || "");
    setBrand(r.brand || "");
    setPriceSource(r.priceSource || "mrp");
    setType(r.type || "percent");
    setValue(String(r.value || ""));
    setEffectiveFrom(r.effectiveFrom || "");
    setEffectiveTo(r.effectiveTo || "");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    await deleteDoc(doc(db, "partyDiscounts", id));
    fetchRules();
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-3">Party Discounts (Brand / Party rules)</h2>

      <div className="grid grid-cols-6 gap-2 items-end mb-3">
        <div className="col-span-1">
          <label className="text-xs">Party ID (leave blank for all)</label>
          <input className="w-full border px-2 py-1 text-sm" value={partyId} onChange={e => setPartyId(e.target.value)} placeholder="party doc id or leave empty" />
        </div>
        <div className="col-span-1">
          <label className="text-xs">Brand</label>
          <input className="w-full border px-2 py-1 text-sm" value={brand} onChange={e => setBrand(e.target.value)} placeholder="brand name or leave empty" />
        </div>
        <div>
          <label className="text-xs">Price Source</label>
          <select className="w-full border px-2 py-1 text-sm" value={priceSource} onChange={e => setPriceSource(e.target.value)}>
            <option value="mrp">MRP</option>
            <option value="rate">Item Rate</option>
            <option value="cost">Cost</option>
          </select>
        </div>
        <div>
          <label className="text-xs">Type</label>
          <select className="w-full border px-2 py-1 text-sm" value={type} onChange={e => setType(e.target.value)}>
            <option value="percent">Percent (%)</option>
            <option value="amount">Amount</option>
          </select>
        </div>
        <div>
          <label className="text-xs">Value</label>
          <input className="w-full border px-2 py-1 text-sm" value={value} onChange={e => setValue(e.target.value)} />
        </div>
        <div>
          <label className="text-xs">From</label>
          <input type="date" className="w-full border px-2 py-1 text-sm" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} />
        </div>

        <div className="col-span-1">
          <label className="text-xs">To</label>
          <input type="date" className="w-full border px-2 py-1 text-sm" value={effectiveTo} onChange={e => setEffectiveTo(e.target.value)} />
        </div>

        <div className="col-span-2 flex gap-2">
          <button onClick={handleSave} className="px-3 py-1 bg-blue-600 text-white rounded" disabled={saving}>
            {saving ? "Saving..." : (editingId ? "Update Rule" : "Create Rule")}
          </button>
          <button onClick={resetForm} className="px-3 py-1 border rounded">Reset</button>
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-2">Existing Rules</h3>
        {loading ? <div>Loading…</div> : (
          <table className="w-full text-sm border">
            <thead className="bg-gray-100 text-xs">
              <tr>
                <th className="p-2">Party</th><th>Brand</th><th>Price Source</th><th>Type</th><th>Value</th><th>From</th><th>To</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                  <td className="p-2">{r.partyId || "All"}</td>
                  <td className="p-2">{r.brand || "All"}</td>
                  <td className="p-2">{r.priceSource}</td>
                  <td className="p-2">{r.type}</td>
                  <td className="p-2">{r.value}</td>
                  <td className="p-2">{r.effectiveFrom}</td>
                  <td className="p-2">{r.effectiveTo || "∞"}</td>
                  <td className="p-2 space-x-1">
                    <button onClick={() => handleEdit(r)} className="text-xs px-2 py-1 border rounded">Edit</button>
                    <button onClick={() => handleDelete(r.id)} className="text-xs px-2 py-1 border rounded text-red-600">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
